import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { Button, CircularProgress } from '@mui/material';
import { DashboardContainer } from '../../Containers/DashboardLayout';
import { Toasts } from '../../Components/Toasts/Toasts';
import { clearCursorCache, fetchAllUsers, onUpdateUser, useGetCountOfTable } from './saga/saga/saga';

const PAGE_SIZE = 25;

export const Users = () => {
  const [page, setPage] = useState(0);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const { count } = useGetCountOfTable('users');

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const items = await fetchAllUsers({ page: p, docLimit: PAGE_SIZE });
      setList((prev) => (p === 0 ? items : [...prev, ...items]));
      if (items.length < PAGE_SIZE) setExhausted(true);
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(0);
    return () => clearCursorCache();
  }, [load]);

  const onLoadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const patch = async (id, data, successMsg) => {
    try {
      await onUpdateUser({ userId: id, data });
      setList((prev) => prev.map((u) => (u.id === id ? { ...u, ...data } : u)));
      Toasts.success(successMsg);
    } catch (e) {
      Toasts.error(e?.response?.data?.message || e?.message || 'update failed');
    }
  };

  return (
    <DashboardContainer>
      <div className="p-4">
        <h3 className="text-[22px] font-bold">
          Users {count !== null && count > 0 && ` - ${count}`}
        </h3>

        <div className="mt-2 bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-left text-[14px]" style={{ minWidth: 820 }}>
            <thead className="bg-gray-50 text-gray-600 text-[12px] uppercase">
              <tr>
                <th className="px-3 py-2 whitespace-nowrap">name</th>
                <th className="px-3 py-2 whitespace-nowrap">email</th>
                <th className="px-3 py-2 whitespace-nowrap">phone</th>
                <th className="px-3 py-2 whitespace-nowrap">joined</th>
                <th className="px-3 py-2 whitespace-nowrap">last seen</th>
                <th className="px-3 py-2 whitespace-nowrap">status</th>
                <th className="px-3 py-2 whitespace-nowrap">actions</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && !loading ? (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                    No users yet.
                  </td>
                </tr>
              ) : (
                list.map((u) => (
                  <tr key={u.id} className="border-t border-gray-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {u.name || <span className="text-gray-400">—</span>}
                      {u.isAdmin && (
                        <span className="ml-2 text-[11px] bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                          admin
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                      {u.email || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-600">
                      {u.phone || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {moment(u.createdAt).fromNow()}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500">
                      {u.lastSeenAt ? moment(u.lastSeenAt).fromNow() : '—'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {u.isBlocked ? (
                        <span className="text-red-600 font-semibold">blocked</span>
                      ) : u.isDeactivated ? (
                        <span className="text-gray-500">deactivated</span>
                      ) : (
                        <span className="text-green-700">active</span>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex gap-1">
                        {(u.isBlocked || u.isDeactivated) ? (
                          <Button
                            size="small"
                            onClick={() =>
                              patch(u.id, { isBlocked: false, isDeactivated: false }, 'Unblocked')
                            }
                          >
                            Unblock
                          </Button>
                        ) : (
                          <>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => patch(u.id, { isBlocked: true }, 'Blocked')}
                            >
                              Block
                            </Button>
                            <Button
                              size="small"
                              color="warning"
                              onClick={() => patch(u.id, { isDeactivated: true }, 'Deactivated')}
                            >
                              Deactivate
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center mt-4">
          {loading ? (
            <CircularProgress size={20} />
          ) : exhausted ? (
            <span className="text-gray-400 text-[13px]">No more rows.</span>
          ) : (
            <Button onClick={onLoadMore} variant="outlined" size="small">
              Load more
            </Button>
          )}
        </div>
      </div>
    </DashboardContainer>
  );
};
